import {
  cloneElement,
  isValidElement,
  useEffect,
  useId,
  useState,
  type ReactElement,
  type ReactNode,
} from 'react';

interface ChildProps {
  'aria-describedby'?: string;
  onFocus?: (e: unknown) => void;
  onBlur?: (e: unknown) => void;
  onMouseEnter?: (e: unknown) => void;
  onMouseLeave?: (e: unknown) => void;
}

/** Tooltip for a focusable child; appears on hover and focus, dismissable with Escape (WCAG 1.4.13). */
export function Tooltip({
  content,
  children,
}: {
  content: ReactNode;
  children: ReactElement<ChildProps>;
}) {
  const id = useId();
  const [open, setOpen] = useState(false);
  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => e.key === 'Escape' && setOpen(false);
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [open]);
  if (!isValidElement(children)) throw new Error('Tooltip needs a single focusable element child');
  const p = children.props;
  const trigger = cloneElement(children, {
    'aria-describedby': open
      ? [p['aria-describedby'], id].filter(Boolean).join(' ')
      : p['aria-describedby'],
    onFocus: (e: unknown) => {
      p.onFocus?.(e);
      setOpen(true);
    },
    onBlur: (e: unknown) => {
      p.onBlur?.(e);
      setOpen(false);
    },
    onMouseEnter: (e: unknown) => {
      p.onMouseEnter?.(e);
      setOpen(true);
    },
    onMouseLeave: (e: unknown) => {
      p.onMouseLeave?.(e);
      setOpen(false);
    },
  });
  return (
    <span className="relative inline-flex">
      {trigger}
      {open ? (
        <span role="tooltip" id={id} className="fl-tooltip">
          {content}
        </span>
      ) : null}
    </span>
  );
}
